/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import AuthState from "../../models/interfaces/global-state";
import { useStore } from "../../store/useStore";
import getFirebase from "../firebase";
import OrganizationService from "../services/organization-service";
import UserService from "../services/user-service";
import NotificationService from "../services/notification-service";
import { DateTime } from "luxon";
import OrganizationSubscriptionService from "../services/organization-subscription-service";
import moment from "moment";
import { PlanType } from "../../models/enumerations/plan-type";

const { auth } = getFirebase();

interface AuthStateContextProps {
    state: AuthState;
    setState: Dispatch<SetStateAction<AuthState>>;
}

export const AuthStateContext = createContext({} as AuthStateContextProps)

export const AuthStateProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const initialState: AuthState = localStorage.getItem('state')
        ?
        JSON.parse(localStorage.getItem('state')!).organization ? JSON.parse(localStorage.getItem('state')!) : {}
        : {}

    const [state, setState] = useState(initialState);
    const setOrganization = useStore((state) => state.setOrganization);
    const setUser = useStore((state) => state.setUser);

    if (initialState.organization) {
        setOrganization(initialState.organization);
    }

    if (initialState.user) {
        setUser(initialState.user);
    }

    // used to check if user has logged in and lastLoggedIn isn't today.
    // if so, update their lastLoggedIn to today.
    // Used to keep users within the "Active" status.
    useEffect(() => {
        const today = DateTime.now().toFormat("yyyy-MM-dd");

        const updateUserLastLoggedIn = async () => {
            if (!state.user?.id) {
                return;
            }
            // we want to get fb user in case we didn't manage state somewhere correctly and then save invalid data
            const user = await UserService.get(state.user.id);
            if (!user) {
                return;
            }
            user.lastLoggedIn = today
            UserService.update(user);

            await setUser(user);
            setState((state) => ({
                ...state, ...{ user: user }
            }));

        }
        if (state.authenticated && state.user && ((state.user.lastLoggedIn && state.user.lastLoggedIn !== today) || !state.user.lastLoggedIn)) {
            updateUserLastLoggedIn();
        }

    }, [state.authenticated, state.user?.lastLoggedIn])

    useEffect(() => {
        auth.onAuthStateChanged(async (firebaseUser) => {
            const idTokenResult = await auth.currentUser?.getIdTokenResult();
            if (idTokenResult == null) {
                setState((state) => ({
                    ...state, ...{
                        authenticated: false,
                        authUser: null,
                        notifications: null,
                        dashboard: null,
                        claims: { roles: [], superAdmin: false }
                    }
                }));
                return;
            }
            // if ((idTokenResult.claims.roles?.length || 0) === 0 && !idTokenResult.claims.superAdmin) {
            //     console.error("User is not a portal user");
            //     return;
            // }

            if (state.user && !state.user.roles) {
                setState((state) => ({
                    ...state, ...{
                        authenticated: false,
                        authUser: null,
                        notifications: null,
                        dashboard: null,
                        claims: { roles: [], superAdmin: false }
                    }
                }));
                return;
            }

            // idTokenResult.claims.roles does not exist anymore for some reason, we are now using state to get roles/isSuperAdmin
            setState((state) => ({
                ...state, ...{
                    authenticated: firebaseUser != null,
                    authUser: firebaseUser,
                    notifications: state.notifications ?? null,
                    claims: { roles: state.user?.roles!, superAdmin: state.user?.isSuperAdmin === true },
                }
            }));

            if (firebaseUser == null) {
                return;
            }

            if (state.user?.authenticationId && firebaseUser.uid === state.user?.authenticationId) {
                setOrgAndRunChecks(state.user.organizationId)
                return;
            }

            const users = await UserService.getBy([{
                field: "email",
                operator: "==",
                value: firebaseUser.email,
            }]);

            if (users == null || users.length !== 1) {
                return;
            }
            const user = users[0];

            await setUser(user);
            setState((state) => ({
                ...state, ...{ user: user }
            }));
            setOrgAndRunChecks(user.organizationId)

        });
    }, [setOrganization, setState, setUser, state.user?.authenticationId]);

    useEffect(() => {
        if (state.authenticated && !state.notifications && state.user && state.user.id && state.user.organizationId) {
            initialNotificationLoad(state.user.id, state.user.organizationId)
        }
    }, [state])

    useEffect(() => {
        if (!state.authenticated) {
            localStorage.setItem('state', JSON.stringify({ temp: state.temp }));
        }
        else {
            localStorage.setItem('state', JSON.stringify(state));
        }

    }, [initialState.organization, setOrganization, state]);

    const initialNotificationLoad = async (userId: string, userOrgId: string) => {
        const notificationsList = await NotificationService.getNotifications(userId, userOrgId);
        setState((state) => ({
            ...state, ...{
                notifications: {
                    data: notificationsList,
                    count: notificationsList.filter((item: any) => item.read === false).length > 99
                        ? '99+'
                        : notificationsList?.filter((item: any) => item.read === false).length
                }
            }
        }));
    }

    const setOrgAndRunChecks = async (organizationId?: string) => {
        if (!organizationId) {
            return
        }
        const org = await OrganizationService.get(organizationId);
        if (org != null) {
            await setOrganization(org);
            setState((state) => ({
                ...state, ...{ organization: org }
            }));

            if (org.id && org.plan?.value === PlanType.Trial) {
                // for some reason if you await this, it takes too long and other components run expecting organization to be set in state
                // these values aren't needed until later when a user navigates to billing. It should be ok
                // to let this run in the background as its not needed instantly.
                OrganizationSubscriptionService.getByOrganizationId(org.id).then((orgSub) => {
                    let redTrialWarning: boolean = false;
                    let yellowTrialWarning: boolean = false;
                    if (orgSub != null && orgSub.length > 0) {
                        const targetDate = moment(orgSub[0].renewDate); // Parse the ISO string
                        const currentDate = moment(); // Get the current date

                        const daysAway = targetDate.diff(currentDate, 'days');
                        if (daysAway >= 0) {
                            // if its 3 days or less and warning doesn't already exist
                            if (daysAway < 4 && orgSub[0].redTrialWarning == null) {
                                redTrialWarning = true
                            }
                            // if its 7 days or less and warning doesn't already exist
                            else if (daysAway < 8 && orgSub[0].yellowTrialWarning == null) {
                                yellowTrialWarning = true
                            }
                        }
                        setState((state) => ({
                            ...state,
                            ...{ yellowTrialWarning: yellowTrialWarning, redTrialWarning: redTrialWarning },
                        }));
                    }
                })
            }

        }
    }

    return (
        <AuthStateContext.Provider value={{ state, setState }}>
            {children}
        </AuthStateContext.Provider>
    );
}

export const useAuthState = () => {
    const context = useContext(AuthStateContext);
    if (!context) {
        throw new Error("useGlobalState must be used within a AuthStateContext");
    }
    return context;
};

export const authLogout = async () => {
    await auth.signOut();
}